import React, { FC, MouseEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Link, List, Typography } from '@mui/material';
import { darken } from '@mui/material/styles';

import {
  Network,
  SegmentEventType,
  UpdateUserProfileOnboardingMutation,
  UpdateUserProfileOnboardingMutationVariables,
  useReconcileExpressActivatedUsersMutation,
  useUpdateUserProfileOnboardingMutation,
} from 'api';
import {
  ClientLogo,
  ConnectAccount,
  Connection,
  Layouts,
  Loaders,
  PaddedPaper,
  SocialIcon,
} from 'components';
import {
  useCurrentUser,
  useNetworkCapabilities,
  useProtectedClient,
  useSocialConnections,
} from 'hooks';
import { useUpdateCurrentUser } from 'updaters';
import * as paths from 'paths';
import theme from 'theme';
import { connectionPopup, getAppImage } from 'helpers';

import { useTrackingQuery } from 'app-react-query/hooks';
import { AccountMarker } from 'features/header';
import { LOCAL_STORAGE_KEY_ACCESS_KEY } from 'app-constants';
import { useListPublicSocialConnectionsQuery } from 'api/public';

const styles = {
  button: {
    padding: theme.spacing(),
    color: 'common.white',
    '&:hover': {
      color: 'common.white',
    },
  },

  twitter: {
    backgroundColor: theme.custom.networks.x,
    '&:hover': {
      backgroundColor: darken(theme.custom.networks.x, 0.1),
    },
  },

  linkedin: {
    backgroundColor: theme.custom.networks.linkedin,
    '&:hover': {
      backgroundColor: darken(theme.custom.networks.linkedin, 0.1),
    },
  },
} as const;
type Update = UpdateUserProfileOnboardingMutation;
type UpdateVariables = UpdateUserProfileOnboardingMutationVariables;

const OnboardingSocials: FC = () => {
  const navigate = useNavigate();
  const client = useProtectedClient();
  const currentUser = useCurrentUser();
  const { hasTwitter } = useNetworkCapabilities();
  const { connections } = useSocialConnections();
  const { t } = useTranslation();
  const [getConnections, setConnections] = useState(connections);
  const [reconcile, setReconcile] = useState(false);

  const track = useTrackingQuery();
  const updateOptions = useUpdateCurrentUser<Update, UpdateVariables>();

  const { data: updated_connections } = useListPublicSocialConnectionsQuery(
    getSocialConnectionsVariables(),
    {
      enabled: reconcile,
    }
  );

  function getSocialConnectionsVariables() {
    const accessKey = localStorage.getItem(LOCAL_STORAGE_KEY_ACCESS_KEY);
    return accessKey !== 'null' ? { access_key: accessKey } : {};
  }

  const { mutateAsync, isLoading } = useUpdateUserProfileOnboardingMutation({
    onSuccess: data => {
      const socialConnectionNames = getConnections.map(connection => connection.provider);
      track(
        SegmentEventType.SignupLoginNewUserSignupClientUserModifiedConnectNetwork,
        data.updateUserProfileOnboarding.id,
        {
          user_networks: socialConnectionNames,
        }
      );
    },
    ...updateOptions,
  });

  const { mutateAsync: reconcileMutation } = useReconcileExpressActivatedUsersMutation();

  useEffect(() => {
    const callReconcile = async () => {
      try {
        await reconcileMutation({
          users: [
            {
              user_id: currentUser.id,
              access_key: localStorage.getItem(LOCAL_STORAGE_KEY_ACCESS_KEY),
            },
          ],
        });
      } catch (error) {
        console.error('Failed to reconcile: ', error);
        return;
      }
      setReconcile(true);
    };

    if (getConnections.length == 0) {
      void callReconcile();
    }
  }, []);

  useEffect(() => {
    if (updated_connections) {
      setConnections(updated_connections.listPublicSocialConnections.items);
      setReconcile(false);
    } else {
      setConnections(connections);
    }
  }, [updated_connections, connections]);

  async function handleContinue(event: MouseEvent) {
    event.preventDefault();

    await mutateAsync({
      data: {
        id: currentUser.id,
        last_onboarding_step: 3,
        onboarding_completed: true,
      },
    });

    navigate(paths.setupProfile);
  }

  function renderConnections() {
    return getConnections.map(account => <Connection key={account.id} account={account} />);
  }

  function renderImage() {
    return <img width={500} height={500} {...getAppImage('illustrations/board.png', 500)} />;
  }

  function renderMain() {
    return (
      <PaddedPaper elevation={0} padding="64px 48px">
        <Layouts.OnboardingBox>
          <Box textAlign="center">
            <ClientLogo client={client} />
          </Box>
          <Typography align="center" paragraph variant="h2">
            {t('onboarding:socialsHeader')}
          </Typography>
          <Typography align="center" paragraph color="textSecondary">
            {t('onboarding:socialsSubHead')}
          </Typography>
          {!!getConnections.length && (
            <Box paddingTop={3}>
              <List>{renderConnections()}</List>
            </Box>
          )}
          <ConnectAccount network={Network.Linkedin}>
            <Box paddingTop={3}>
              <Button
                fullWidth
                variant="contained"
                sx={{ ...styles.button, ...styles.linkedin }}
                onClick={() => connectionPopup(Network.Linkedin)}
                startIcon={<SocialIcon state="inverted" provider="linkedin" size={24} />}
              >
                {t('user:connectToLinkedIn')}
              </Button>
            </Box>
          </ConnectAccount>

          {hasTwitter && (
            <ConnectAccount network={Network.Twitter}>
              <Box paddingTop={2}>
                <Button
                  fullWidth
                  variant="contained"
                  sx={{ ...styles.button, ...styles.twitter }}
                  onClick={() => connectionPopup(Network.Twitter)}
                  startIcon={<SocialIcon state="inverted" provider="twitter" size={24} />}
                >
                  {t('user:connectToX')}
                </Button>
              </Box>
            </ConnectAccount>
          )}

          <Box paddingTop={4} textAlign="center">
            {!isLoading && (
              <Link href="#" onClick={handleContinue} color="textSecondary" underline="hover">
                {getConnections.length ? t('onboarding:doneForNow') : t('onboarding:skipForNow')}
              </Link>
            )}
            {isLoading && <Loaders.Standard height={17} />}
          </Box>

          <AccountMarker />
        </Layouts.OnboardingBox>
      </PaddedPaper>
    );
  }

  return <Layouts.Onboarding image={renderImage()} main={renderMain()} />;
};

export default OnboardingSocials;
